import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'
import { TrainingHeatmap } from '@goodfellas/training-heatmap-ui'
import type { TrainingHeatmapData } from '@goodfellas/training-history'

import { goodfellasApi } from '../../apis'
import { fetchMyGlobalCard, setFeedbackData } from '../../functions/dataHelpers'
import { normalizeBeltColor, normalizeDegree } from '../../functions/graduation'
import {
  useCompetitionStore,
  useFeedbackStore,
  useStore,
} from '../../functions/store'
import EvaluationCTA from './EvaluationCTA'
import GraduationHistory from './GraduationHistory'
import ProfileAiSummary from './ProfileAiSummary'
import ShareApp from './ShareApp'
import CompetitionSummaryCard from './competition/CompetitionSummaryCard'
import GraduationModal from './graduation/GraduationModal'

interface DashboardHomeProps {
  onNavigate: (step: string) => void
}

export default function DashboardHome({ onNavigate }: DashboardHomeProps) {
  const user = useStore((state) => state)
  const { stats, competitionActions } = useCompetitionStore()
  const userStore = useStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const ratings = feedbackStore.ratings
  const { t } = useTranslation('avaliacao360')
  const ts = (key: string, fallback: string) => String(t(key, fallback))

  const [isGradOpen, setIsGradOpen] = useState(false)

  const beltColor = React.useMemo(
    () => normalizeBeltColor(user.belt),
    [user.belt],
  )
  const beltDegree = React.useMemo(
    () => normalizeDegree(beltColor, user.degree),
    [beltColor, user.degree],
  )

  // Fetch global card (stats globais do perfil – agrega todos os grupos)
  const { data: myGlobalCard, isLoading: myCardLoading } = useQuery({
    queryKey: ['globalCard', user.phoneNumber],
    queryFn: fetchMyGlobalCard,
    enabled: !!user.phoneNumber,
  })

  // Fetch competition stats
  const { data: competitionData, isLoading: competitionLoading } = useQuery({
    queryKey: ['competition', user.phoneNumber],
    queryFn: async () => {
      const response = await goodfellasApi.get(
        `/competition/matches/phone/${encodeURIComponent(user.phoneNumber)}`,
      )
      if (!response.data.success) {
        throw new Error('Failed to fetch competition data')
      }
      return response.data.data
    },
    enabled: !!user.phoneNumber,
  })

  // Derive competition days for heatmap overlay
  const competitionDays = React.useMemo(() => {
    if (!competitionData) return []
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const set = new Set<string>()
    for (const m of competitionData ?? []) {
      const raw = (m as Record<string, unknown>)?.eventDate
      if (!raw) continue
      const d = typeof raw === 'string' ? new Date(raw) : raw
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) continue
      set.add(fmt.format(d))
    }
    return Array.from(set)
  }, [competitionData])

  // Sync competition data to store
  useEffect(() => {
    if (competitionData) {
      competitionActions.setMatches(competitionData)
    }
  }, [competitionData, competitionActions])

  /*useEffect(() => {
    const loadData = async () => {
      if (ratings.length === 0 && userStore.phoneNumber) {
        await setFeedbackData({
          feedbackHook: feedbackStore,
          userData: userStore,
          step: 'home',
        })
      }
    }
    loadData()
  }, [userStore.phoneNumber, userStore.group?._id])*/

  // Fetch training heatmap (me)
  const {
    data: trainingData,
    isLoading: trainingLoading,
    error: trainingQueryError,
  } = useQuery({
    queryKey: ['trainingHistory', user.phoneNumber],
    queryFn: async () => {
      const resp = await goodfellasApi.get('/training/history/me', {
        validateStatus: () => true,
      })
      if (resp.status >= 200 && resp.status < 300 && resp.data?.success) {
        return resp.data.data as TrainingHeatmapData
      }
      throw new Error('Erro ao carregar frequência de treinos')
    },
    enabled: !!user.phoneNumber,
  })

  const trainingError = trainingQueryError?.message ?? null

  // Calculate pending evaluations
  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['pendingEvaluations', user.group?._id],
    queryFn: async () => {
      const groupIdQuery = user.group?._id ? `?groupId=${user.group._id}` : ''
      const response = await goodfellasApi.get<{ pending: number; total: number }>(`/evaluation/feedback/pending/count${groupIdQuery}`)
      return response.data
    },
    enabled: !!user.phoneNumber,
  })

  const pendingEvaluations = pendingData?.pending ?? 0
  const totalEvaluation = pendingData?.total ?? 0

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Olá, {user.name || 'Atleta'}!
            </h2>
            <p className="text-slate-400 text-sm sm:text-base">
              Bem-vindo à Avaliação 360
            </p>
          </div>
          <div className="flex items-center gap-3">
            {beltColor && (
              <>
                <GraduationModal
                  isOpen={isGradOpen}
                  phoneNumber={String(user.phoneNumber)}
                  groupId={user.group?._id ? String(user.group._id) : ''}
                  initialName={String(user.name || '')}
                  initialType={String(user.type || '')}
                  initialBelt={(beltColor as Colors) || Colors.White}
                  initialDegree={beltDegree}
                  onClose={() => setIsGradOpen(false)}
                  onSaved={({ belt, degree }) => {
                    user.setUser({
                      ...user,
                      belt,
                      degree,
                    })
                  }}
                />
                <button
                  type="button"
                  onClick={() => setIsGradOpen(true)}
                  className="rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 hover:opacity-95 transition-opacity"
                  title="Editar graduação"
                  disabled={!user.group?._id}
                >
                  <BeltVisual
                    color={(beltColor as Colors) || Colors.White}
                    degree={beltDegree}
                    size="md"
                    animated
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Training Heatmap */}
      <div>
        <TrainingHeatmap
          data={trainingData ?? null}
          loading={trainingLoading}
          error={trainingError}
          belt={beltColor}
          competitionDays={competitionDays}
          labels={{
            title: ts('dashboard.trainingFrequency', 'Frequência de treinos'),
            subtitle: ts('dashboard.last12Months', 'Últimos 12 meses'),
            total: ts('dashboard.totalTrainings', 'Total'),
            weeklyAvg: ts('dashboard.weeklyAvg', 'Média/semana'),
            currentStreak: ts('dashboard.currentStreak', 'Streak atual'),
            bestStreak: ts('dashboard.bestStreak', 'Melhor streak'),
            less: ts('dashboard.less', 'Menos'),
            more: ts('dashboard.more', 'Mais'),
            training: ts('dashboard.training', 'treino'),
            trainings: ts('dashboard.trainings', 'treinos'),
          }}
        />
      </div>

      {/* AI Summary */}
      <ProfileAiSummary
        phoneNumber={user.phoneNumber}
        displayName={user.name || undefined}
      />

      {/* Competition Summary Card */}
      <CompetitionSummaryCard
        stats={stats}
        loading={competitionLoading}
        onViewDetails={() => onNavigate('competition')}
      />

      {/* Graduation History */}
      <GraduationHistory />

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Meu card 360 (global) – stats globais do perfil */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl p-4 sm:p-5 border border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500/30 to-amber-600/20 rounded-xl flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">
                  {ts('dashboard.myGlobalCard', 'Meu card 360')}
                </h3>
                <p className="text-xs text-slate-400">
                  {myCardLoading
                    ? ts('dashboard.loading', 'Carregando...')
                    : myGlobalCard
                      ? ts(
                          'dashboard.globalCardHint',
                          'Avaliações de todos os grupos',
                        )
                      : ts(
                          'dashboard.globalCardEmpty',
                          'Receba avaliações para gerar',
                        )}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate('cards')}
              className="text-xs text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1"
            >
              {ts('dashboard.viewCard', 'Ver card')}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* No card yet: invite friends so they evaluate you and the card
              gets generated. */}
          {!myCardLoading && !myGlobalCard && (
            <div className="mt-4 border-t border-white/10 pt-4 flex flex-col items-center gap-2">
              <p className="text-xs text-slate-400 text-center">
                {ts(
                  'shareApp.cardHint',
                  'Chame a galera pra treinar e te avaliar — seu card é gerado a partir das avaliações.',
                )}
              </p>
              <ShareApp />
            </div>
          )}
        </div>

        {/* Evaluation Progress Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl p-4 sm:p-5 border border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500/30 to-sky-600/20 rounded-xl flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-sky-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Avaliações</h3>
                <p className="text-xs text-slate-400">
                  {pendingEvaluations > 0
                    ? `${pendingEvaluations} pendentes`
                    : 'Todas completas'}
                </p>
              </div>
            </div>
            <button
              onClick={() => onNavigate('feedback')}
              className="text-xs text-slate-400 hover:text-sky-400 transition-colors flex items-center gap-1"
            >
              Avaliar
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-2">
              {pendingLoading ? (
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>Carregando...</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Progresso</span>
                    <span>
                      {totalEvaluation - pendingEvaluations}/
                      {totalEvaluation}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-sky-500 to-sky-400 transition-all duration-500"
                      style={{
                        width: `${
                          totalEvaluation > 0
                            ? ((totalEvaluation - pendingEvaluations) / totalEvaluation) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
        </div>

        {/* Fighter Profile Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl p-4 sm:p-5 border border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500/30 to-violet-600/20 rounded-xl flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-violet-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Meu Perfil</h3>
                <p className="text-xs text-slate-400">Página do Atleta</p>
              </div>
            </div>
            <button
              onClick={() => onNavigate('profile')}
              className="text-xs text-slate-400 hover:text-violet-400 transition-colors flex items-center gap-1"
            >
              Ver
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Evaluation CTA */}
      <EvaluationCTA onStartEvaluation={() => onNavigate('feedback')} />
    </div>
  )
}
